import { Box, Button, Select, Stack, VStack, Link } from "@chakra-ui/react"
import React, { memo, useCallback, useMemo, useState } from "react"
import TileCarousel from "../TileCarousel/TileCarousel"
import { useCart } from "@app/hooks/useCart"
import { useAppContext } from "@app/providers/app"
import { useRoutes } from "@app/hooks/useRoutes"
import { useConfigContext } from "@app/providers/config"
import ProductQuantity from "@app/components/Product/ProductQuantity"
import ProductPrice from "./ProductPrice"
import ProductWishlist from "@app/components/Product/ProductWishlist"
import Image from "@app/components/Image/Image"

const ProductShopTheLookProduct: React.FC<any> = ({ product }) => {
  const { addToCart, loading: cartLoading } = useCart()
  const { dispatch } = useAppContext()

  const {
    settings: { routes },
  } = useConfigContext()

  const { urlResolver } = useRoutes()
  const link = urlResolver(product, routes.PRODUCT)

  const carouselSettings = {
    enableNavigation: false,
    enablePagination: true,
    enableScrollbar: true,
    enableLoop: false,
    enableAutoPlay: false,
  }
  const iconButtonProps: BoxProps = {
    pos: "absolute",
    top: "15px",
    right: "10px",
    zIndex: 5,
  }

  const sizes = useMemo(
    () =>
      product?.variants?.map(variant => ({
        ...variant,
        id: variant?.id,
        availableForSale: variant?.availableForSale,
        size: variant?.selectedOptions?.find(option => option?.name === "Size")?.value,
      })),
    [product]
  )
  const [selectedSize, setSelectedSize] = useState(sizes?.[0]?.id)
  const [quantity, setQuantity] = useState<number>(1)

  const onIncrement = (amount: number) => setQuantity(qty => qty + amount)
  const onDecrement = (amount: number) => setQuantity(qty => Math.max(1, qty - amount))

  const parsedSelectedSize = useMemo(() => sizes?.find(size => size?.id === selectedSize), [selectedSize, sizes])

  const triggerMiniCart = () => {
    dispatch({
      type: "cart",
      payload: {
        activeCart: true,
        addedToCart: true,
      },
    })
  }

  const handleAddToCart = useCallback(async () => {
    await addToCart(selectedSize, quantity, [])
    triggerMiniCart()
  }, [addToCart, selectedSize, triggerMiniCart])

  const selectedProduct = product?.variants?.filter(item => item.id === selectedSize)
  return (
    <Stack key={product?.id} direction={{ base: `column`, sm: `row`, md: `row` }} alignItems={`center`} gap={2}>
      <Box
        width={{ base: `100%`, sm: `200px`, md: `200px` }}
        maxWidth={`100%`}
        border={`1px solid #D3D1CF`}
        borderRadius={`sm`}
        overflow={`hidden`}
        position="relative"
        sx={{
          ".swiper-slide": {
            width: `100%`,
            maxWidth: `100%`,
          },
          ".swiper-pagination-bullet": {
            width: `10px`,
            height: `10px`,
            borderRadius: `50%`,
            border: `1px solid #D3D1CF!important`,
            backgroundColor: `transparent!important`,
          },
          ".swiper-pagination-bullet-active": {
            backgroundColor: `#D3D1CF!important`,
          },
        }}
      >
        <ProductWishlist product={product} size={"sm"} {...iconButtonProps} display={"block"} />

        <TileCarousel carouselSettings={carouselSettings} margin={false}>
          {product?.images?.slice(0, -1).map(image => <Image key={image?.id} src={image?.src} alt={image?.altText} />)}
        </TileCarousel>
      </Box>
      <VStack alignItems={`flex-start`} gap={1} maxWidth={{ base: "unset", sm: "200px" }} width={{ base: "100%", sm: "200px" }}>
        <Link href={link.url} size={`lg`} fontWeight={600} width="100%">
          {product?.title}
        </Link>
        <ProductPrice variant={parsedSelectedSize} direction={`row`} />
        <Box display="flex" width={{ base: "100%", sm: "200px" }}>
          <Select onChange={e => setSelectedSize(e.target?.value)} width={{ base: "inherit", sm: "inherit" }} mr={{ base: "5px", sm: "0" }}>
            {sizes?.map(size => (
              <option key={size?.id} value={size?.id}>
                {size?.size}
              </option>
            ))}
          </Select>
          <Box display={{ base: `block`, sm: `none`, md: `none` }}>
            <ProductQuantity value={quantity} min={1} onIncrement={onIncrement} onDecrement={onDecrement} size="md" />
          </Box>
        </Box>

        <Button
          width={"100%"}
          variant={`green`}
          onClick={handleAddToCart}
          isLoading={cartLoading}
          isDisabled={selectedProduct[0]?.availableForSale == false ? true : false}
        >
          {selectedProduct[0]?.availableForSale == false ? "UNAVAILABLE" : "Add To Cart"}
        </Button>
      </VStack>
    </Stack>
  )
}

export default memo(ProductShopTheLookProduct)
